import { useCallback, useMemo, useState } from "react";
import {
  AdminBulkUpsertResponse,
  AdminManagedUser,
  checkAdminUsers,
  upsertAdminUsers,
} from "../../../services/platformApi";
import { extractNormalizedEmails } from "../utils";

export const useBulkUserManagement = () => {
  const [bulkEmailsInput, setBulkEmailsInput] = useState("");
  const [selectedBulkProducts, setSelectedBulkProducts] = useState<string[]>([]);
  const [bulkCheckLoading, setBulkCheckLoading] = useState(false);
  const [bulkCreateLoading, setBulkCreateLoading] = useState(false);
  const [bulkCheckResults, setBulkCheckResults] = useState<AdminManagedUser[]>([]);
  const [bulkCreateResponse, setBulkCreateResponse] = useState<AdminBulkUpsertResponse | null>(null);
  const [bulkError, setBulkError] = useState("");
  const [isBulkProductDropdownOpen, setBulkProductDropdownOpen] = useState(false);

  const normalizedBulkEmails = useMemo(
    () => extractNormalizedEmails(bulkEmailsInput),
    [bulkEmailsInput]
  );

  const toggleBulkProductSelection = useCallback((productKey: string) => {
    setSelectedBulkProducts((current) =>
      current.includes(productKey)
        ? current.filter((value) => value !== productKey)
        : [...current, productKey]
    );
  }, []);

  const handleBulkCheck = useCallback(async () => {
    if (normalizedBulkEmails.length === 0) {
      setBulkError("Įklijuokite bent vieną galiojantį el. pašto adresą.");
      return;
    }

    setBulkCheckLoading(true);
    setBulkError("");
    setBulkCreateResponse(null);

    try {
      const results = await checkAdminUsers(bulkEmailsInput);
      setBulkCheckResults(results);
    } catch (error) {
      setBulkError(error instanceof Error ? error.message : "Nepavyko patikrinti el. pašto adresų");
      setBulkCheckResults([]);
    } finally {
      setBulkCheckLoading(false);
    }
  }, [bulkEmailsInput, normalizedBulkEmails.length]);

  const handleBulkCreate = useCallback(async () => {
    if (normalizedBulkEmails.length === 0) {
      setBulkError("Įklijuokite bent vieną galiojantį el. pašto adresą.");
      return;
    }

    if (selectedBulkProducts.length === 0) {
      setBulkError("Pasirinkite bent vieną produktą.");
      return;
    }

    setBulkCreateLoading(true);
    setBulkError("");

    try {
      const response = await upsertAdminUsers(bulkEmailsInput, selectedBulkProducts);
      setBulkCreateResponse(response);
      setBulkCheckResults([]);
    } catch (error) {
      setBulkError(error instanceof Error ? error.message : "Nepavyko sukurti paskyrų");
      setBulkCreateResponse(null);
    } finally {
      setBulkCreateLoading(false);
    }
  }, [bulkEmailsInput, normalizedBulkEmails.length, selectedBulkProducts]);

  return {
    bulkEmailsInput,
    setBulkEmailsInput,
    selectedBulkProducts,
    bulkCheckLoading,
    bulkCreateLoading,
    bulkCreateResponse,
    bulkError,
    bulkCheckResults,
    normalizedBulkEmails,
    isBulkProductDropdownOpen,
    setBulkProductDropdownOpen,
    toggleBulkProductSelection,
    handleBulkCheck,
    handleBulkCreate,
    resultRows: bulkCreateResponse?.results || bulkCheckResults,
  };
};
